import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import useAdminProfile from "../hooks/useAdminProfile";
import AdminSidebar from "../components/adminDashboard/Sidebar";
import AdminTopbar from "../components/adminDashboard/Topbar";
import AdminMobileDock from "../components/mobile/AdminMobileDock";

export default function AdminDashboardLayout({ children, activeNav = "dashboard" }) {
  const [expanded, setExpanded] = useState(false);
  const [activeId, setActiveId] = useState(activeNav);
  const [isMobile, setIsMobile] = useState(() => typeof window !== "undefined" && window.innerWidth < 768);
  const adminProfile = useAdminProfile();
  const [avatarFailed, setAvatarFailed] = useState(false);
  useEffect(() => { setAvatarFailed(false); }, [adminProfile.imageUrl]);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  return (
    <div
      style={{
        fontFamily: "'Inter', sans-serif",
        background: "#FFF8EE",
        minHeight: "100vh",
        position: "relative",
        overflowX: "hidden",
      }}
    >
      {/* ── Google Fonts ── */}
      <link rel="preconnect" href="https://fonts.googleapis.com" />
      <link
        href="https://fonts.googleapis.com/css2?family=Poppins:wght@700;800;900&family=Inter:wght@400;500;600;700&display=swap"
        rel="stylesheet"
      />

      {/* ── Ambient background orbs ── */}
      <motion.div
        animate={{ y: [0, -20, 0], x: [0, 14, 0] }}
        transition={{ duration: 14, repeat: Infinity, ease: "easeInOut" }}
        style={{
          position: "fixed",
          width: 500,
          height: 500,
          borderRadius: "50%",
          background: "radial-gradient(circle,rgba(242,154,12,0.07),transparent 70%)",
          filter: "blur(80px)",
          top: -160,
          right: -100,
          pointerEvents: "none",
          zIndex: 0,
        }}
      />
      <motion.div
        animate={{ y: [0, 16, 0], x: [0, -10, 0] }}
        transition={{ duration: 17, repeat: Infinity, ease: "easeInOut" }}
        style={{
          position: "fixed",
          width: 400,
          height: 400,
          borderRadius: "50%",
          background: "radial-gradient(circle,rgba(107,138,90,0.06),transparent 70%)",
          filter: "blur(80px)",
          bottom: -100,
          left: 0,
          pointerEvents: "none",
          zIndex: 0,
        }}
      />

      {/* ── Desktop Left Sidebar (Strictly hidden on mobile, untouched on desktop) ── */}
      <AdminSidebar
        expanded={expanded}
        setExpanded={setExpanded}
        activeId={activeId}
        setActiveId={setActiveId}
      />

      {/* ── Mobile Top Brand Header (Strictly md:hidden) ── */}
      <header className="md:hidden sticky top-0 z-40 flex items-center justify-between px-4 py-2.5 bg-[#FFF8EE]/90 backdrop-blur-md border-b border-[#EFE3D2]/70 shadow-xs">
        <div className="flex items-center gap-2.5">
          <div className="w-8 h-8 rounded-xl overflow-hidden shadow-xs border border-white">
            <img src="/assets/logo.jpeg" alt="Divyyog Admin" className="w-full h-full object-cover" />
          </div>
          <div>
            <h1 className="text-sm font-black text-stone-900 leading-none tracking-tight" style={{ fontFamily: "'Poppins', sans-serif" }}>
              DIVYYOG
            </h1>
            <p className="text-[9px] font-bold text-emerald-800 tracking-widest uppercase mt-0.5">
              Admin Panel
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <span className="flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[9px] font-bold bg-emerald-500/10 text-emerald-700 border border-emerald-500/20">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 inline-block animate-pulse" />
            Online
          </span>
          <Link
            to="/admin/settings"
            aria-label="My profile"
            title={adminProfile.name}
            className="w-8 h-8 rounded-full overflow-hidden flex items-center justify-center text-white text-[11px] font-black shadow-xs border-2 border-white active:scale-95"
            style={{ background: "linear-gradient(135deg,#6B8A5A,#8BA47A)", fontFamily: "'Poppins', sans-serif" }}
          >
            {adminProfile.imageUrl && !avatarFailed
              ? <img src={adminProfile.imageUrl} alt={adminProfile.name} className="w-full h-full object-cover" onError={() => setAvatarFailed(true)} />
              : adminProfile.initials}
          </Link>
        </div>
      </header>

      {/* ── Main content area ── */}
      <motion.main
        animate={{ marginLeft: isMobile ? 0 : (expanded ? 280 : 80) }}
        transition={{ duration: 0.35, ease: [0.25, 1, 0.5, 1] }}
        className="relative z-10 min-h-screen px-3 sm:px-6 lg:px-8 py-2 sm:py-4 pb-28 md:pb-6 w-auto min-w-0 box-border overflow-x-hidden"
      >
        <AdminTopbar />
        <div className="w-full min-w-0">
          {children}
        </div>
      </motion.main>

      {/* ── Creative Mobile Bottom Navigation Dock (Strictly md:hidden) ── */}
      <AdminMobileDock />
    </div>
  );
}